import React, { useState, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  DialogTitle
} from '@material-ui/core'
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  InfoRounded
} from '@material-ui/icons'
import ProgressButton from 'components/ProgressButton'
import { parse } from 'papaparse'
import { downloadAttendeesSampleCSV } from 'utils/downloadCSV'
import { isValidEmail, parseCsvData } from 'utils'
import { CountNumber } from '../../organizations/components/ImportSummaryModal'

const ImportAttendeesModal = ({ open, onClose, onImport }) => {
  const classes = useStyles()
  const inputFileRef = useRef()
  const [csvFile, setCsvFile] = useState(null)
  const [csvError, setCsvError] = useState('')
  const [csvInfoMsg, setCsvInfoMsg] = useState('')
  const [loading, setLoading] = useState(false)

  // New state variables for review functionality
  const [showReviewTable, setShowReviewTable] = useState(false)
  const [parsedData, setParsedData] = useState([])
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [importSummary, setImportSummary] = useState({
    uniqueCount: 0,
    existingCount: 0,
    invalidCount: 0
  })
  const [duplicateCount, setDuplicateCount] = useState(0)

  const getRowErrors = (row) => {
    const errors = new Set()
    if (!row.firstName) errors.add('firstName')
    if (!row.lastName) errors.add('lastName')
    if (!row.email || !isValidEmail(row.email)) errors.add('email')
    return errors
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) {
      setCsvFile(null)
      setShowReviewTable(false)
      return
    }
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setCsvError('Please upload a CSV file')
      setCsvFile(null)
      setShowReviewTable(false)
      return
    }

    setCsvError('')
    setCsvFile(file)

    // Parse the CSV file
    parse(file, {
      header: false,
      skipEmptyLines: 'greedy',
      complete: async (results) => {
        const data = results.data
        const rawRows = parseCsvData(data).map((item) => ({
          ...item,
          email: item.email ? item.email.toLowerCase().trim() : item.email
        }))

        // Normalize roles and mark duplicates
        const seenEmails = new Set()
        const validRoles = ['Attendee', 'Speaker', 'Moderator']
        const parsedRecords = rawRows.map((item) => {
          const role = !item?.role || !validRoles.includes(item?.role) ? 'Attendee' : item.role

          const emailKey = item.email ? item.email : null
          const isDuplicate = emailKey ? seenEmails.has(emailKey) : false
          if (emailKey && !isDuplicate) seenEmails.add(emailKey)

          return { ...item, role, isDuplicate }
        })

        const dupCount = parsedRecords.filter((r) => r.isDuplicate).length
        const invalidCount = parsedRecords.filter((r) => getRowErrors(r).size > 0).length
        const validCount = parsedRecords.filter(
          (r) => !r.isDuplicate && getRowErrors(r).size === 0
        ).length

        if (validCount === 0 && dupCount === 0) {
          setCsvError('The CSV file must contain at least one valid record.')
          setShowReviewTable(false)
        } else {
          setCsvError('')
          setCsvInfoMsg('')
          setParsedData(parsedRecords)
          setShowReviewTable(true)
        }

        setDuplicateCount(dupCount)
        setImportSummary((prev) => ({
          ...prev,
          invalidCount
        }))

        event.target.value = null
      },
      error: (err) => {
        setCsvError(err)
        setShowReviewTable(false)
      }
    })
    setCsvError('')
    setCsvInfoMsg('')
    event.target.value = null
  }

  const handleImport = async () => {
    try {
      setLoading(true)
      const result = await onImport(
        parsedData.filter((item) => !item.isDuplicate && getRowErrors(item).size === 0)
      )

      // Update import summary with actual results
      setImportSummary((prevState) => ({
        ...prevState,
        uniqueCount: result?.added,
        existingCount: result?.existing || 0
      }))

      // Reset the form
      setCsvFile(null)
      setParsedData([])
      setCsvInfoMsg('')
      setShowReviewTable(false)
      onClose()
      // Show the summary modal
      setShowSummaryModal(true)
    } catch (error) {
      console.error('Error importing attendees:', error)
      setCsvError('Error importing attendees')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setCsvFile(null)
    setCsvError('')
    setCsvInfoMsg('')
    setParsedData([])
    setDuplicateCount(0)
    setShowReviewTable(false)
    onClose()
  }

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false)
    onClose()
  }

  return (
    <>
      <Dialog open={open} maxWidth="xl" fullWidth classes={{ paper: classes.dialogPaper }}>
        <Box className={classes.dialogTitle}>
          <Typography variant="h5">Import Regular Attendees from CSV</Typography>
          {!loading && (
            <IconButton onClick={handleClose} size="small" className={classes.closeButton}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        <DialogContent className={classes.dialogContent}>
          <Box className={classes.note}>
            <Box className={classes.noteHeader}>
              <Typography className={classes.noteHead}>
                Instructions to Import Regular Attendees:
              </Typography>
            </Box>

            <Box className={classes.noteSteps}>
              <Box className={classes.stepItem}>
                <Box className={classes.stepNumber}>1</Box>
                <Typography className={classes.stepText}>
                  Prepare a CSV file according to the columns listed below.
                </Typography>
              </Box>

              <Box className={classes.stepItem}>
                <Box className={classes.stepNumber}>2</Box>
                <Typography className={classes.stepText}>
                  Select the appropriate file from your computer.
                </Typography>
              </Box>

              <Box className={classes.stepItem}>
                <Box className={classes.stepNumber}>3</Box>
                <Typography className={classes.stepText}>
                  {`Review the data and click 'Import Attendees' to add all attendees.`}
                </Typography>
              </Box>
            </Box>

            <Box className={classes.fileFormatInfo}>
              <Box>
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1rem'
                  }}>
                  <Typography variant="subtitle2" className={classes.formatTitle}>
                    Sample CSV Format
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    className={classes.sampleDownloadButton}
                    onClick={downloadAttendeesSampleCSV}
                    startIcon={<DownloadIcon />}>
                    Download Sample CSV
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.formatHeaderCell}>
                          First Name <span className={classes.required}>*</span>
                        </TableCell>
                        <TableCell className={classes.formatHeaderCell}>
                          Last Name <span className={classes.required}>*</span>
                        </TableCell>
                        <TableCell className={classes.formatHeaderCell}>
                          Email <span className={classes.required}>*</span>
                        </TableCell>
                        <TableCell className={classes.formatHeaderCell}>
                          Role <span className={classes.required}>*</span>
                        </TableCell>
                        <TableCell className={classes.formatHeaderCell}>Credential</TableCell>
                        <TableCell className={classes.formatHeaderCell}>Position</TableCell>
                        <TableCell className={classes.formatHeaderCell}>Organization</TableCell>
                        <TableCell className={classes.formatHeaderCell}>Phone</TableCell>
                        <TableCell className={classes.formatHeaderCell}>City</TableCell>
                        <TableCell className={classes.formatHeaderCell}>State</TableCell>
                        <TableCell className={classes.formatHeaderCell}>Country</TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
                <Box className={classes.roleInfoBox}>
                  <InfoIcon className={classes.roleInfoIcon} />
                  <Typography className={classes.roleInfoText}>
                    {`Role must be one of the following: 'Attendee', 'Speaker', or 'Moderator'. Any other role will default to 'Attendee'. To import Organization Representatives, please use the Organization module.`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box mt={3}>
            <input
              type="file"
              id="csv-file-input"
              accept=".csv"
              onChange={handleFileChange}
              className={classes.input}
              ref={inputFileRef}
            />
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button
                variant="outlined"
                component="span"
                className={classes.upload}
                startIcon={<CloudUploadIcon />}
                onClick={() => inputFileRef.current.click()}>
                {csvFile ? csvFile.name : 'Select CSV file'}
              </Button>
              {csvFile && (
                <IconButton
                  size="small"
                  title="Clear selected file"
                  disabled={loading}
                  onClick={() => {
                    setCsvFile(null)
                    setCsvError('')
                    setCsvInfoMsg('')
                    setShowReviewTable(false)
                    setParsedData([])
                    setDuplicateCount(0)
                    setImportSummary((prev) => ({ ...prev, invalidCount: 0 }))
                    if (inputFileRef.current) inputFileRef.current.value = null
                  }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {csvError && <Typography className={classes.error}>{csvError}</Typography>}

            {csvInfoMsg && (
              <Box className={classes.infoBox}>
                <InfoIcon className={classes.infoIcon} />
                <Typography className={classes.info}>
                  {csvInfoMsg.replace('Representative', 'Attendee')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Review Data Table */}
          {showReviewTable && parsedData.length > 0 && (
            <Box className={classes.tableContainer}>
              <Typography className={classes.tableTitle}>
                Review Attendees Data ({parsedData.length})
              </Typography>

              {importSummary.invalidCount > 0 && (
                <Box className={classes.warningBanner}>
                  <InfoRounded className={classes.warningIcon} />
                  <Typography className={classes.warningText}>
                    <strong>
                      {importSummary.invalidCount} row
                      {importSummary.invalidCount !== 1 ? 's' : ''} have invalid or missing required
                      fields.
                    </strong>{' '}
                    These rows will be <strong>skipped</strong> during import — only valid rows will
                    be imported. You can also fix the CSV and re-upload.
                  </Typography>
                </Box>
              )}

              {duplicateCount > 0 && (
                <Box className={classes.duplicateBanner}>
                  <InfoRounded className={classes.duplicateIcon} />
                  <Typography className={classes.duplicateText}>
                    <strong>
                      {duplicateCount} duplicate email{duplicateCount !== 1 ? 's' : ''} found.
                    </strong>{' '}
                    Duplicate rows will be <strong>automatically skipped</strong> — only the first
                    occurrence of each email is kept.
                  </Typography>
                </Box>
              )}

              {importSummary.invalidCount === 0 && duplicateCount === 0 && (
                <Box className={classes.importInfo}>
                  <Box className={classes.infoNumberCircle}>i</Box>
                  <Typography className={classes.importInfoText}>
                    Please review the data before importing. Only attendees with valid information
                    will be imported.
                  </Typography>
                </Box>
              )}

              <TableContainer component={Paper} className={classes.reviewTable}>
                <Table size="small">
                  <TableHead className={classes.reviewHeader}>
                    <TableRow>
                      <TableCell className={classes.reviewHeaderCell}>First Name</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Last Name</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Email</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Role</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Credential</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Position</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Organization</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Phone</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>City</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>State</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Country</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedData?.map((row, index) => {
                      const errors = getRowErrors(row)
                      const rowInvalid = errors.size > 0
                      const borderColor = rowInvalid
                        ? '#f44336'
                        : row.isDuplicate
                        ? '#ff9800'
                        : 'transparent'
                      return (
                        <TableRow
                          key={index}
                          style={
                            rowInvalid || row.isDuplicate
                              ? { borderLeft: `3px solid ${borderColor}` }
                              : {}
                          }>
                          <TableCell
                            className={`${classes.reviewCell} ${
                              errors.has('firstName') ? classes.cellError : ''
                            }`}>
                            {row.firstName || <span className={classes.missingValue}>Missing</span>}
                          </TableCell>
                          <TableCell
                            className={`${classes.reviewCell} ${
                              errors.has('lastName') ? classes.cellError : ''
                            }`}>
                            {row.lastName || <span className={classes.missingValue}>Missing</span>}
                          </TableCell>
                          <TableCell
                            className={`${classes.reviewCell} ${
                              errors.has('email')
                                ? classes.cellError
                                : row.isDuplicate
                                ? classes.cellDuplicate
                                : ''
                            }`}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {row.email || <span className={classes.missingValue}>Missing</span>}
                              {row.isDuplicate && (
                                <span className={classes.duplicateBadge}>Duplicate</span>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell className={classes.reviewCell}>
                            {row.role || 'Attendee'}
                          </TableCell>
                          <TableCell className={classes.reviewCell}>
                            {row.credential || '-'}
                          </TableCell>
                          <TableCell className={classes.reviewCell}>
                            {row.position || '-'}
                          </TableCell>
                          <TableCell className={classes.reviewCell}>
                            {row.organization || '-'}
                          </TableCell>
                          <TableCell className={classes.reviewCell}>{row.phone || '-'}</TableCell>
                          <TableCell className={classes.reviewCell}>{row.city || '-'}</TableCell>
                          <TableCell className={classes.reviewCell}>{row.state || '-'}</TableCell>
                          <TableCell className={classes.reviewCell}>{row.country || '-'}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box className={classes.noticeBox} mt={3}>
                <Typography className={classes.noticeText}>
                  All attendees will be imported with status{' '}
                  <span className={classes.bold}>{`'Not Notified'`}</span>. Notification emails can
                  be sent later.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <Box className={classes.buttonBox}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <ProgressButton
            label="Import Attendees"
            onClick={handleImport}
            disabled={parsedData.length === 0 || loading}
            inProgress={loading}
            variant="contained"
            color="primary"
          />
        </Box>
      </Dialog>

      {/* Import Summary Modal */}
      <Dialog
        open={showSummaryModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 3,
          className: classes.dialogPaper
        }}>
        <DialogTitle>Import Summary</DialogTitle>
        <DialogContent>
          <Box className={classes.summaryContainer}>
            <Box className={classes.summaryBox}>
              <Box className={classes.summaryItem}>
                <Box className={classes.summaryIconSuccess}>
                  <CountNumber
                    value={importSummary.uniqueCount}
                    className={classes.summaryNumber}
                  />
                </Box>
                <Box className={classes.summaryTextContainer}>
                  <Typography className={classes.summaryText}>
                    Attendee(s) successfully imported
                  </Typography>
                </Box>
              </Box>

              <Box className={classes.summaryItem}>
                <Box className={classes.summaryIconSkipped}>
                  <CountNumber
                    value={importSummary.existingCount}
                    className={classes.summaryNumber}
                  />
                </Box>
                <Box className={classes.summaryTextContainer}>
                  <Typography className={classes.summaryText}>
                    Attendee(s) skipped due to existing email addresses
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box className={classes.buttonBox} style={{ justifyContent: 'center' }}>
              <Button
                onClick={handleCloseSummaryModal}
                variant="contained"
                color="primary"
                className={classes.okButton}>
                OK
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}

const useStyles = makeStyles({
  roleInfoBox: {
    marginTop: '16px',
    backgroundColor: 'rgba(255, 193, 7, 0.1)',
    padding: '12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    borderLeft: '4px solid #ffc107'
  },
  tableTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: '16px'
  },
  reviewTable: {
    marginBottom: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    borderRadius: '8px',
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    maxHeight: '300px',
    position: 'relative'
  },
  reviewHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#f5f9ff'
  },
  reviewHeaderCell: {
    backgroundColor: '#f5f9ff',
    color: '#0277bd',
    fontWeight: 600,
    padding: '10px 12px',
    fontSize: '13px',
    borderBottom: '1px solid #e0e0e0'
  },
  reviewCell: {
    padding: '10px 12px',
    fontSize: '13px',
    color: '#455a64',
    borderBottom: '1px solid #f0f0f0'
  },
  roleInfoIcon: {
    color: '#f57c00',
    marginTop: '3px',
    fontSize: '18px'
  },
  roleInfoText: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#5d4037',
    lineHeight: '1.5'
  },
  dialogPaper: {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
  },
  dialogTitle: {
    borderBottom: '1px solid #f0f0f0',
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& h5': {
      fontWeight: 600,
      fontSize: '20px',
      color: '#2c3e50'
    }
  },
  closeButton: {
    color: '#757575'
  },
  dialogContent: {
    padding: '24px 24px 16px'
  },
  modalTitle: {
    letterSpacing: '0.5px',
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: '16px'
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '1rem',
    borderRadius: '8px'
  },
  rowKey: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#455a64'
  },
  rowValue: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#03a9f4'
  },
  summaryBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  noticeBox: {
    backgroundColor: 'rgba(3, 169, 244, 0.05)',
    borderLeft: '4px solid #03a9f4',
    padding: '12px',
    borderRadius: '8px'
  },
  noticeText: {
    fontSize: '15px',
    letterSpacing: '0.4px',
    lineHeight: '1.6'
  },
  note: {
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: 'rgba(3, 169, 244, 0.08)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    marginBottom: '24px',
    border: '1px solid rgba(3, 169, 244, 0.2)'
  },
  noteHeader: {
    marginBottom: '16px'
  },
  noteHead: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#2c3e50',
    lineHeight: 1.5
  },
  noteSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#03a9f4',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
    flexShrink: 0
  },
  stepText: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#37474f',
    lineHeight: 1.6,
    paddingTop: '4px'
  },
  fileFormatInfo: {
    marginLeft: '40px',
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginTop: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: '12px',
    borderRadius: '8px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
  },
  formatItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    '& > p': {
      fontWeight: 600,
      color: '#455a64',
      marginBottom: '2px'
    }
  },
  formatTags: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  chipTag: {
    backgroundColor: '#e3f2fd',
    color: '#0277bd',
    fontWeight: 500,
    border: '1px solid #90caf9'
  },
  sampleDownloadButton: {
    marginTop: '8px',
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 500,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    borderColor: '#03a9f4',
    color: '#0277bd',
    '&:hover': {
      backgroundColor: '#e3f2fd',
      borderColor: '#0277bd',
      boxShadow: '0 2px 5px rgba(3, 169, 244, 0.15)'
    }
  },
  buttonBox: {
    borderTop: '1px solid #efefef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    gap: '12px',
    padding: ' 16px 24px'
  },
  upload: {
    textTransform: 'none',
    flex: 1,
    justifyContent: 'flex-start',
    padding: '14px 16px',
    fontSize: '14px',
    fontWeight: 500,
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    background: '#FFFFFF',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#03a9f4',
      boxShadow: '0 2px 5px rgba(3, 169, 244, 0.15)'
    }
  },
  optionalChip: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
    fontWeight: 500,
    border: '1px solid #ce93d8'
  },
  input: {
    display: 'none'
  },
  error: {
    marginTop: '8px',
    marginLeft: '12px',
    color: '#f44336',
    fontWeight: 500
  },
  infoIcon: {
    marginTop: '4px',
    fontSize: '18px',
    color: '#03a9f4'
  },
  infoBox: {
    marginTop: '8px',
    marginLeft: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(3, 169, 244, 0.08)',
    borderRadius: '8px',
    border: '1px solid rgba(3, 169, 244, 0.2)'
  },
  info: {
    marginTop: '0',
    color: '#0288d1',
    fontWeight: 500
  },
  bold: {
    fontWeight: 600
  },
  formatTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    color: '#455a64'
  },
  reviewTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: '16px'
  },
  tableContainer: {
    marginTop: '24px',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0'
  },
  tableHeaderCell: {
    backgroundColor: '#f5f5f5',
    color: '#333',
    fontWeight: 600,
    fontSize: '14px',
    borderBottom: '2px solid #e0e0e0'
  },
  tableCell: {
    fontSize: '14px',
    color: '#555',
    borderBottom: '1px solid #e0e0e0',
    padding: '12px 16px'
  },
  validIcon: {
    color: '#4caf50',
    fontSize: '20px'
  },
  invalidIcon: {
    color: '#f44336',
    fontSize: '20px'
  },
  reviewButton: {
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    padding: '10px 20px',
    borderRadius: '8px',
    backgroundColor: '#03a9f4',
    color: 'white',
    '&:hover': {
      backgroundColor: '#0288d1'
    }
  },
  summaryDialogPaper: {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
  },
  summaryDialogTitle: {
    padding: '16px 24px',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
    '& h2': {
      fontSize: '18px',
      fontWeight: 600,
      color: '#2c3e50'
    }
  },
  summaryDialogContent: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  summaryText: {
    fontSize: '16px',
    color: '#333',
    lineHeight: 1.5
  },
  summarySubText: {
    fontWeight: 600,
    marginBottom: '8px',
    fontSize: '16px',
    color: '#455a64'
  },
  summaryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px'
  },
  summaryLabel: {
    fontSize: '14px',
    color: '#777',
    fontWeight: 500
  },
  summaryValue: {
    fontSize: '14px',
    color: '#333',
    fontWeight: 600
  },
  summaryDialogActions: {
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  },
  required: {
    fontWeight: 'bold',
    fontSize: '18px',
    color: '#f44336'
  },
  importInfo: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f5f9ff',
    borderRadius: '8px',
    border: '1px solid rgba(3, 169, 244, 0.2)',
    margin: '16px 0px'
  },
  infoNumberCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#03a9f4',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
    marginRight: '12px'
  },
  importInfoText: {
    fontSize: '14px',
    color: '#455a64',
    fontWeight: 500
  },
  summaryContainer: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  summaryIconSuccess: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    backgroundColor: 'rgba(46, 204, 113, 0.15)',
    border: '2px solid #2ecc71',
    flexShrink: 0,
    padding: '4px'
  },
  summaryIconSkipped: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    backgroundColor: 'rgba(3, 169, 244, 0.15)',
    border: '2px solid #03a9f4',
    flexShrink: 0,
    padding: '4px'
  },
  summaryIconError: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    backgroundColor: 'rgba(244, 67, 54, 0.15)',
    border: '2px solid #f44336',
    flexShrink: 0,
    padding: '4px'
  },
  summaryNumber: {
    marginBottom: '4px',
    fontWeight: 600,
    color: '#2c3e50',
    display: 'block',
    width: '100%',
    textAlign: 'center'
  },
  summaryTextContainer: {
    flex: 1
  },
  formatHeaderCell: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #6f6f6f'
  },
  cellError: {
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    color: '#c62828',
    fontWeight: 600
  },
  missingValue: {
    color: '#f44336',
    fontStyle: 'italic',
    fontWeight: 600,
    fontSize: '12px'
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(244, 67, 54, 0.07)',
    border: '1px solid rgba(244, 67, 54, 0.3)',
    borderRadius: '8px',
    margin: '0 0 16px'
  },
  warningIcon: {
    color: '#f44336',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '1px'
  },
  warningText: {
    fontSize: '13px',
    color: '#b71c1c',
    lineHeight: 1.6
  },
  cellDuplicate: {
    backgroundColor: 'rgba(255, 152, 0, 0.12)',
    color: '#e65100',
    fontWeight: 600
  },
  duplicateBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(255, 152, 0, 0.08)',
    border: '1px solid rgba(255, 152, 0, 0.4)',
    borderRadius: '8px',
    margin: '0 0 16px'
  },
  duplicateIcon: {
    color: '#e65100',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '1px'
  },
  duplicateText: {
    fontSize: '13px',
    color: '#4e2600',
    lineHeight: 1.6
  },
  duplicateBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 152, 0, 0.2)',
    color: '#e65100',
    fontWeight: 700,
    fontSize: '11px',
    border: '1px solid rgba(255, 152, 0, 0.5)',
    whiteSpace: 'nowrap'
  },
  okButton: {
    padding: '8px 40px',
    fontWeight: 600
  }
})

export default ImportAttendeesModal
